import ButtonDesigned from '@UI-Elements/Buttons/ButtonDesigned';
import { HtmlTooltip, ToolTipCircle } from '@UI-Elements/ToolTipCircle';
import {
  formatToPriceInDollars,
  FormatToPriceTemplate,
  FormatToShortPriceTemplate,
  formatToShortString,
} from '@utils/FormatValue';
import React from 'react';
import { trendDirectionType } from '../../sp-types/InventoryType';
import { IRiskPercent } from '../../sp-types/accountTypes';

import { LinearGauge, LinearGaugeProps } from '@UI-Elements/Charts/LinearGauge/LinearGauge';
import { getExposureColor, getExposureStatus } from '@utils/RiskUtils';
import { IMaterialExposure } from '../../sp-types/netExposureType';
import { LoadingLogoComponent } from '../Quotes/LoadingLogoComponent';
import { getExposureCalculation } from './dashboard-components/ExposeCalculation';
import { Material } from '../../sp-types';
import { getCurrencySymbol, useCurrencyRates } from '../../hooks/useCurrencyRates';
import { CurveMiniChart } from '../HistoricalPrices/components/CurveMiniChart';
import { getHistoryPricesSince } from '../../sp-firebase/historyPricesFB';
import { getDateBefore } from '../../utils/gridUtils';
import { getCurveForIndex, PricePoint } from '../HistoricalPrices/components/HistoricalUtils';
import { CircularProgress } from '@mui/material';
import { getUnitObj } from '../../utils/MassUnits/massUnitsUtils';
import { ToggleChips } from './dashboard-components/ToggleChips';
import { MULTIPLE_MATS, NO_DATA } from './dashboardUtils';

interface ExposureSingleCardProps {
  trendDirection?: trendDirectionType;
  mainTitle: string;
  secondaryTitle: string;
  amount: string | React.ReactNode;
  amountColor?: string;
  descriptionTitle?: string;
  toolTipText: string | any;
  ttPlace?: string | any;
}

const ExposureSingleCard: React.FC<ExposureSingleCardProps> = ({
  amount,
  mainTitle,
  secondaryTitle,
  toolTipText,
  ttPlace,
  amountColor,
  descriptionTitle,
  trendDirection,
}) => {
  const arrowDirectionDictionary: { [key: string]: string } = {
    appreciation: '/assets/icons/colored/green-triangle.svg',
    depreciation: '/assets/icons/colored/red-triangle.svg',
    flat: '',
  };

  return (
    <div>
      <div className="flex flex-row space-x-[0.75rem] pb-2 ">
        <h4 className="text-lg">{mainTitle}</h4>
        <div>
          {ttPlace ? (
            <HtmlTooltip text={toolTipText} placement={ttPlace} />
          ) : (
            <ToolTipCircle text={toolTipText} lgSize="lg" xlgSize="lg" />
          )}
        </div>
      </div>
      <h6 className="text-xs text-gray-450">{secondaryTitle}</h6>
      <div className="flex  flex-row space-x-[0.75rem]">
        <p style={{ color: amountColor || 'black' }} className="text-2xl m-0">
          {amount}
        </p>
        {trendDirection && (
          <div className="flex flex-col justify-center ">
            <img className="w-[0.75rem] h-[0.75rem]  " src={arrowDirectionDictionary[trendDirection]} alt="" />
          </div>
        )}
      </div>
      {descriptionTitle && <p>{descriptionTitle}</p>}
    </div>
  );
};

interface ExposureLinerGaugeCardProps {
  linearGaugeProps: LinearGaugeProps; // Props for LinearGauge component
}

export const ExposureLinerGaugeCard: React.FC<ExposureLinerGaugeCardProps> = ({ linearGaugeProps }) => {
  return (
    <div className="px-[1rem] grow ">
      <LinearGauge {...linearGaugeProps} />
    </div>
  );
};

export interface IExposureCardProps {
  gaugeBarProps: LinearGaugeProps;
  netExposure: number;
  totalAmounts: IMaterialExposure;
  materialPriceStatus?: trendDirectionType;
  riskPercent: IRiskPercent;
  selectedMaterial?: string;
  onBalanceNowClickAction?: () => void;
  isBalanceNowLoading?: boolean;
  isExposureLoading?: boolean;
  mainTitle?: string;
  balanceButton?: 'center' | 'right' | 'hide';
  showExposureDetails?: boolean;
  onExpDetailClk?: () => void;
  material?: Material;
  isMiniView?: boolean;
}

export const ExposureCard: React.FC<IExposureCardProps> = ({
  gaugeBarProps,
  netExposure,
  totalAmounts,
  materialPriceStatus,
  riskPercent,
  onBalanceNowClickAction,
  isBalanceNowLoading = false,
  isExposureLoading = false,
  mainTitle = 'Exposure',
  balanceButton = 'center',
  showExposureDetails = false,
  onExpDetailClk,
  material,
  isMiniView = false,
}) => {
  const { pricesMap } = useCurrencyRates();
  const [percentage, setPercentage] = React.useState<number>(riskPercent?.riskBackTesting?.[0]?.value || 0);
  const [futurePrices, setFuturePrices] = React.useState<PricePoint[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (riskPercent?.riskBackTesting?.length) {
      setPercentage(riskPercent?.riskBackTesting?.[0].value || 0);
    }
  }, [riskPercent]);

  const exposureColor = getExposureColor(getExposureStatus(gaugeBarProps.riskTolerance, netExposure));

  React.useEffect(() => {
    const fetchFuturePrices = async () => {
      setLoading(true);
      const fromDate = getDateBefore(10);

      const prices = await getHistoryPricesSince(material?.id.replace('c1', '').replace('C1', '') || '', fromDate);
      if (prices?.length) {
        const curveData = getCurveForIndex(prices.length - 1, new Date(), prices);

        setFuturePrices(curveData);
      } else {
        setFuturePrices([]);
      }
      setLoading(false);
    };

    if (material?.id) {
      fetchFuturePrices();
    }
  }, [material?.id]);

  const descriptionTitle =
    netExposure !== 0
      ? material?.id
        ? `${formatToShortString(Math.abs(netExposure / pricesMap[material.id]))} ${
            getUnitObj(material?.unit).shortForm
          }`
        : MULTIPLE_MATS
      : NO_DATA;

  const cardProps1: ExposureSingleCardProps = {
    amount: FormatToShortPriceTemplate(netExposure),
    descriptionTitle,
    mainTitle,
    secondaryTitle: 'Net exposure',
    toolTipText: getExposureCalculation({
      netExposure,
      totalAmounts,
      exposureColor,
    }),
    ttPlace: 'right',
    amountColor: getExposureColor(getExposureStatus(gaugeBarProps.riskTolerance, netExposure)),
    trendDirection: materialPriceStatus,
  };

  const exposureValue = FormatToShortPriceTemplate(Math.abs((netExposure * (percentage || 1)) / 100));

  return (
    <div className="flex flex-1 gap-3 overflow-auto">
      <div className="flex shadow-md bg-white p-3 flex-1">
        <div className="flex flex-1 justify-between ">
          <div className={`flex flex-row ${isMiniView ? 'flex-1' : 'basis-6/12'}`}>
            <div className="relative">
              <ExposureSingleCard {...cardProps1} />
              {isExposureLoading && (
                <div className="absolute inset-0  flex items-center justify-center bg-white opacity-75   z-50">
                  {<LoadingLogoComponent />}
                </div>
              )}
            </div>
            <div className="flex flex-col flex-1   min-w-[25rem] gap-2 px-3 justify-end ">
              <div className="flex flex-grow-1">
                <ExposureLinerGaugeCard linearGaugeProps={gaugeBarProps} />
              </div>

              {balanceButton === 'center' && (
                <div className="w-full flex flex-row-reverse justify-between px-3">
                  {onBalanceNowClickAction && (
                    <ButtonDesigned
                      type="button"
                      style="primary"
                      disableButton={isBalanceNowLoading || isExposureLoading}
                      onButtonClick={onBalanceNowClickAction}
                      isFunctionLoading={isBalanceNowLoading}
                    >
                      Balance now
                    </ButtonDesigned>
                  )}
                  {showExposureDetails && (
                    <ButtonDesigned type="button" style="outline" onButtonClick={() => onExpDetailClk?.()}>
                      Detailed exposure
                    </ButtonDesigned>
                  )}
                </div>
              )}
            </div>
          </div>

          {!isMiniView && (
            <>
              <div className="flex flex-row border-l-2 w-48  border-l-gray-350 pl-2 justify-between">
                <ExposureSingleCard
                  mainTitle="Volatility risk"
                  amount={<span className="text-lg">{riskPercent.volatility}</span>}
                  toolTipText="exposure"
                  secondaryTitle="Annual volatility"
                />
              </div>
              <div className="flex flex-row basis-2/12 border-l-2 border-l-gray-350 px-2 gap-4">
                <div className="flex flex-col flex-1 gap-2 items-center">
                  <h4 className="text-lg whitespace-nowrap">Loss potential</h4>

                  <div className="flex flex-col gap-0 flex-1 align-center justify-center ">
                    <h6 className="flex text-xs text-gray-450 ">
                      <span className="w-16">% value</span>
                      <span className="w-16">$ value</span>
                    </h6>

                    <div className="flex w-full text-sm">
                      <span className="w-16">{percentage}%</span>
                      <span>{exposureValue}</span>
                    </div>
                  </div>
                  <div className="flex">
                    <ToggleChips
                      selected={percentage}
                      setSelected={setPercentage}
                      chips={riskPercent?.riskBackTesting}
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col flex-1 gap-2 items-center border-l-2 border-l-gray-350 pl-2">
                {material?.name && (
                  <div className="flex flex-1 gap-2">
                    <div className="flex flex-col flex-1 gap-1">
                      <h4 className="text-lg w-32">Price per {getUnitObj(material?.unit).shortForm}</h4>
                      <h6 className="flex text-xs text-gray-450 ">Value:</h6>
                      <p>
                        {FormatToPriceTemplate(material?.currentPrice, getCurrencySymbol(material?.currency || ''))}
                      </p>
                      <p>
                        {material?.currency &&
                          material.currency !== 'USD' &&
                          `(${formatToPriceInDollars(pricesMap[material.id])})`}
                      </p>
                    </div>
                    <div className="flex flex-1 flex-col">
                      <h6 className="flex text-xs text-gray-450 ">Trend</h6>
                      <div className="flex w-36 h-[5.5rem] items-center justify-center *border-1">
                        {loading ? (
                          <div className="flex items-center justify-center">
                            <CircularProgress size={32} />
                          </div>
                        ) : (
                          <CurveMiniChart
                            routePrices={futurePrices}
                            cssClass="w-32 h-24"
                            hideLegend
                            currency={getCurrencySymbol(material?.currency || '')}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
          {['hide', 'right'].includes(balanceButton) && (
            <div className="flex flex-col justify-center ms-20 p-3 w-56">
              {balanceButton === 'right' && onBalanceNowClickAction && (
                <ButtonDesigned
                  type="button"
                  style="primary"
                  disableButton={isBalanceNowLoading}
                  onButtonClick={onBalanceNowClickAction}
                  isFunctionLoading={isBalanceNowLoading}
                >
                  Balance now
                </ButtonDesigned>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
